import {
  MainPageContainer,
  UpperInfoContainer,
} from 'components/PageLayout';
import { createReportURL } from 'core/routes';
import { useRepairGroups, useWorkInProgressV3Report } from 'pages/hooks';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { RepairerGroupsResult } from 'api/resources/models/AutoGenerated';
import { errorMessages } from 'api/resources/responseMessages/errorMessages';
import { AxiosError } from 'axios';
import {
  columns,
  reportHeaderTagLine,
  WORK_IN_PROGRESS_REPORT,
  WORK_IN_PROGRESS_REPORT_DESCRIPTION,
  WORK_IN_PROGRESS_REPORT_TITLE,
} from './constants';
import { ReportsLoader } from 'components/ReportsLoader';
import { NoReportDataMessage } from 'components/ReportsComponents/NoReportDataMessage';
import { ReportGridContainer } from 'components/ReportGridContainer';
import { ReportBackButton } from 'components/ReportsComponents/ReportBackButton';
import { ReportHeader } from 'components/ReportsComponents/ReportHeader';
import { WorkInProgressSearchForm } from 'components/ReportForms/WorkInProgressSearchForm';
import { ReportProps } from 'pages/ReportsDetails/hooks';
import { reportLogger } from 'pages/ReportsDetails/reportLogger';
import ReportGridNew from 'components/ReportGrid_New';
import { noPDFexportFormats } from 'components/ReportGrid_New/constants';

export function WorkInProgress({
  reportPath,
  setErrorMessage,
  setToast,
}: ReportProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const urlParamsValue = {
    repairGroup: urlParams.get('repairGroup'),
  };

  const queryEnabled = !!urlParamsValue.repairGroup;

  const { repairerGroups, isRepairGroupsLoading } = useRepairGroups((error) =>
    handleError(error, 'isRepairerGroup')
  );
  const { reportData, refetchReport, isReportFetching } =
    useWorkInProgressV3Report(
      { repairerGroup: urlParamsValue.repairGroup },
      queryEnabled,
      (error) => handleError(error, 'isReport')
    );

  const rows = useMemo(() => reportData, [reportData]);
  // const rows = mockedReportData;

  const [repairGroup, setRepairGroup] = useState<string | null>(
    urlParamsValue.repairGroup || null
  );
  const options = repairerGroups?.map((e: RepairerGroupsResult, i: number) => {
    return { id: i, value: e.repairerGroupValue, text: e.repairerGroup };
  });

  useEffect(() => {
    navigateIfPossible();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParamsValue.repairGroup]);

  const isBusy = isReportFetching;
  const isOptionsLoading = isRepairGroupsLoading;

  return (
    <MainPageContainer isLoading={isBusy} direction="column" wrap="nowrap">
      <UpperInfoContainer direction="column">
        <ReportBackButton />
        <ReportHeader
          reportName={WORK_IN_PROGRESS_REPORT}
          infoMessage={WORK_IN_PROGRESS_REPORT_DESCRIPTION}
        />
        {isOptionsLoading ? (
          <ReportsLoader loadingRowsCount={2} />
        ) : (
          <WorkInProgressSearchForm
            handleSubmit={handleSubmit}
            options={options}
            repairGroup={repairGroup}
            setRepairGroup={setRepairGroup}
            isBusy={isBusy}
          />
        )}
      </UpperInfoContainer>

      {!isBusy && rows?.length === 0 && (
        <NoReportDataMessage />
      )}

      <ReportGridContainer direction="column">
        <ReportGridNew
          columns={columns}
          displayGroupingPanel
          exportFormats={noPDFexportFormats}
          headerTitleForSheet={WORK_IN_PROGRESS_REPORT}
          isLoading={isReportFetching}
          reportHeaderTagLine={reportHeaderTagLine}
          rows={rows}
          title={WORK_IN_PROGRESS_REPORT_TITLE}
        />
      </ReportGridContainer>
    </MainPageContainer>
  );

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (urlParamsValue.repairGroup == repairGroup) refetchReport();
    else navigateIfPossible();
  }

  function navigateIfPossible() {
    if (repairGroup)
      navigate(createReportURL({ repairGroup: repairGroup }, reportPath, true), {
        replace: true,
      });
  }

  function handleError(
    error: AxiosError,
    type: 'isRepairerGroup' | 'isReport'
  ) {
    reportLogger(error);
    setErrorMessage(
      type == 'isRepairerGroup'
        ? errorMessages.repairersList
        : errorMessages.reports(WORK_IN_PROGRESS_REPORT)
    );
    setToast(true);
  }
}
